import { useMembership } from "@/hooks/use-membership";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import type { PlainOrganizationPolicies } from "@/queries/organization-queries";
import type { AccountMembership } from "gitpod-next-api/gitpod/v1/account_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";

/**
 * Logic function to determine if a user requires a project to create an environment
 * Exported for testing purposes
 */
export function requireProjects(
    membership: AccountMembership | undefined,
    policies: PlainOrganizationPolicies | undefined,
): boolean {
    if (!membership) return false;

    // Admins can create environments without projects
    if (membership.userRole === OrganizationRole.ADMIN) {
        return false;
    }

    // For regular members, check organization policies
    return !!policies?.membersRequireProjects;
}

/**
 * Hook to determine whether the current user requires a project to create an environment based on:
 * - User's membership role (admin = false)
 * - Organization policies for regular members
 */
export const useRequireProject = (): { isPending: boolean; requireProjects: boolean } => {
    const { membership, isPending: isMembershipPending } = useMembership();
    const { data: policies, isPending: isPoliciesPending } = useGetOrganizationPolicies();

    const isPending = isMembershipPending || isPoliciesPending;

    return {
        isPending,
        requireProjects: requireProjects(membership, policies),
    };
};
